import { createPortal } from 'react-dom';
import { useEffect, useState } from 'react';
import { QuickPay } from '@/components/PaymentButtons/QuickPay';

interface UseQuickPayOptions {
  isBuyNow?: boolean;
  productCode?: string;
  quantity?: number;
}

export const useQuickPay = (options?: UseQuickPayOptions) => {
  const { isBuyNow: initialBuyNow = false, productCode: initialProductCode, quantity: initialQuantity = 1 } = options || {};
  
  const [portalElement, setPortalElement] = useState<Element | null>(null);
  const [isBuyNow, setIsBuyNow] = useState(initialBuyNow);
  const [productCode, setProductCode] = useState(initialProductCode || '');
  const [quantity, setQuantity] = useState(initialQuantity);

  useEffect(() => {
    // Function to find and set up the portal element
    const setupPortal = () => {
      const element = document.querySelector('.quickpay');
      if (element && element !== portalElement) {
        setPortalElement(element);
        
        // Read initial attributes when element is found
        const buyNowAttr = element.getAttribute('data-buy-now');
        const productCodeAttr = element.getAttribute('data-product-code');
        const quantityAttr = element.getAttribute('data-quantity');
        
        if (buyNowAttr !== null) setIsBuyNow(buyNowAttr === 'true');
        if (productCodeAttr !== null) setProductCode(productCodeAttr);
        if (quantityAttr !== null) setQuantity(parseInt(quantityAttr, 10) || 1);
        
        console.log('useQuickPay: Attributes found', {
          buyNowAttr,
          productCodeAttr,
          quantityAttr,
          isBuyNow: buyNowAttr === 'true'
        });
      }
    };

    // Initial check
    setupPortal();

    // Watch for the element to appear if not found initially
    const observer = new MutationObserver((mutations) => {
      // Check if quickpay element was added
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          setupPortal();
          if (portalElement) break;
        }
      }
    });

    // Observe the entire document for added nodes
    observer.observe(document.body, { 
      childList: true, 
      subtree: true 
    });

    return () => observer.disconnect();
  }, [portalElement]);

  // Watch for attribute changes on the portal element
  useEffect(() => {
    if (!portalElement) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') {
          const element = mutation.target as HTMLElement;
          const buyNowAttr = element.getAttribute('data-buy-now');
          const productCodeAttr = element.getAttribute('data-product-code');
          const quantityAttr = element.getAttribute('data-quantity');
          
          if (buyNowAttr !== null) setIsBuyNow(buyNowAttr === 'true');
          if (productCodeAttr !== null) setProductCode(productCodeAttr);
          if (quantityAttr !== null) setQuantity(parseInt(quantityAttr, 10) || 1);
        }
      });
    });

    observer.observe(portalElement, { 
      attributes: true,
      attributeFilter: ['data-buy-now', 'data-product-code', 'data-quantity']
    });

    return () => observer.disconnect();
  }, [portalElement]);

  return portalElement
    ? createPortal(
        <QuickPay 
          isBuyNow={isBuyNow}
          productCode={productCode}
          quantity={quantity}
        />, 
        portalElement
      )
    : null;
};
